import React from 'react';
import { DetailedItem, FloatingLabelInput } from '../../../../../../ui';
import { useEditableForm } from '../hooks/useEditableForm';
import { SettingsCard } from './SettingsCard';

interface SupportContactData {
  supportEmail: string;
  supportPhone: string;
  supportHours: string;
  emergencyContact: string;
}

interface ISupportAndContact {
  onSave?: (data: SupportContactData) => Promise<void> | void;
}

export const SupportAndContact: React.FC<ISupportAndContact> = ({ onSave }) => {
  const initialData: SupportContactData = {
    supportEmail: 'support@acmecorp.com',
    supportPhone: '+1 (555) 123-4567',
    supportHours: 'Mon-Fri 9AM-6PM PST',
    emergencyContact: '+1 (555) 999-0000',
  };

  const { isEditing, formData, isSaving, handleEditToggle, handleSave, handleInputChange } = useEditableForm({
    initialData,
    onSave: async (data) => {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (onSave) {
        await onSave(data);
      }
    },
  });

  const icon = (
    <svg className='w-5 h-5 text-blue-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z' />
    </svg>
  );

  return (
    <SettingsCard
        title='Support and Contact'
        description='Manage your support and contact information'
        icon={icon}
        isEditing={isEditing}
        isSaving={isSaving}
        onEditToggle={handleEditToggle}
        onSave={handleSave}
    >
      { isEditing ? (
        // Edit Mode
        <>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            <FloatingLabelInput
                label='Support Email'
                value={formData.supportEmail}
                onChange={(e) => handleInputChange('supportEmail', e.target.value)}
            />
            <FloatingLabelInput
                label='Support Phone'
                value={formData.supportPhone}
                onChange={(e) => handleInputChange('supportPhone', e.target.value)}
            />
          </div>

          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            <FloatingLabelInput
                label='Support Hours'
                value={formData.supportHours}
                onChange={(e) => handleInputChange('supportHours', e.target.value)}
            />
            <FloatingLabelInput
                label='Emergency Contact'
                value={formData.emergencyContact}
                onChange={(e) => handleInputChange('emergencyContact', e.target.value)}
            />
          </div>
        </>
      ) : (
        // View Mode
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          <div className='p-4 bg-gray-50 rounded-lg'>
            <DetailedItem
                label='Support Email'
                text={formData.supportEmail}
            />
          </div>
          <div className='p-4 bg-gray-50 rounded-lg'>
            <DetailedItem
                label='Support Phone'
                text={formData.supportPhone}
            />
          </div>
          <div className='p-4 bg-gray-50 rounded-lg'>
            <DetailedItem
                label='Support Hours'
                text={formData.supportHours}
            />
          </div>
          <div className='p-4 bg-gray-50 rounded-lg'>
            <DetailedItem
                label='Emergency Contact'
                text={formData.emergencyContact}
                helpertext='This contact will be used for urgent issues outside regular support hours.'
            />
          </div>
        </div>
      ) }
    </SettingsCard>
  );
};
